// src/app/shared/components/session-timeout-modal/session-timeout-modal.component.ts
import {
  Component, ChangeDetectionStrategy,
  inject, computed, effect, ElementRef
} from '@angular/core';
import { SessionService } from '../../../core/auth/session.service';
import { TranslationService } from '../../../core/i18n/translation.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

/**
 * A partir de cuántos segundos restantes la cuenta atrás se anuncia a
 * lectores de pantalla en cada tick. Fuera de esta ventana, el mensaje vivo
 * es estático (solo cambia al abrirse el modal) — anunciar cada segundo
 * durante los 4 minutos completos de aviso haría la aplicación inutilizable
 * con lector de pantalla (§2.1).
 */
const URGENT_ANNOUNCE_THRESHOLD_SECONDS = 10;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

@Component({
  selector: 'app-session-timeout-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    @if (isOpen()) {
      <!-- Backdrop -->
      <div
        class="modal-backdrop"
        aria-hidden="true"
        (click)="onBackdropClick()">
      </div>

      <!-- Dialog -->
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="'session-timeout-title'"
        [attr.aria-describedby]="'session-timeout-desc'"
        (keydown)="onKeydown($event)">

        <div class="modal__header">
          <span class="modal__icon" aria-hidden="true">⏱</span>
          <h2 id="session-timeout-title" class="modal__title">
            {{ 'session.timeout.title' | translate }}
          </h2>
        </div>

        <div class="modal__body">
          <p id="session-timeout-desc">
            {{ 'session.timeout.message' | translate }}
          </p>
          <div class="modal__countdown" aria-hidden="true">
            <span class="countdown__number">{{ countdownLabel() }}</span>
            <span class="countdown__label">{{ 'session.timeout.seconds' | translate }}</span>
          </div>
          <!--
            Región viva separada del número visible de arriba (que se oculta
            a lectores de pantalla con aria-hidden). liveMessage() solo
            cambia de texto al abrir el modal y durante los últimos
            segundos — un aria-live con texto idéntico en cada recálculo no
            vuelve a anunciarse, así que esto no habla cada segundo.
          -->
          <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {{ liveMessage() }}
          </div>
        </div>

        <div class="modal__footer">
          <button
            class="btn btn--primary"
            type="button"
            (click)="onContinue()">
            {{ 'session.timeout.continue' | translate }}
          </button>
          <button
            class="btn btn--ghost"
            type="button"
            (click)="onLogout()">
            {{ 'session.timeout.logout' | translate }}
          </button>
        </div>
      </div>
    }
  `,
  styles: [`
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: var(--overlay-backdrop);
      z-index: var(--z-modal-backdrop);
      backdrop-filter: blur(2px);
    }
    .modal {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: var(--z-modal);
      background: var(--color-white);
      border-radius: var(--border-radius);
      box-shadow: var(--shadow-lg);
      width: min(420px, calc(100vw - 32px));
      padding: var(--space-8);
    }
    .modal__header {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      margin-bottom: var(--space-4);
    }
    .modal__icon { font-size: 28px; }
    .modal__title {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text);
      margin: 0;
    }
    .modal__body { margin-bottom: var(--space-6); }
    .modal__body p {
      color: var(--color-text-light);
      font-size: var(--font-size-base);
      line-height: var(--line-height-loose);
      margin-bottom: var(--space-5);
    }
    .modal__countdown {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--space-4);
      background: var(--color-warning-light);
      border-radius: var(--border-radius);
      gap: var(--space-1);
    }
    .countdown__number {
      font-size: calc(var(--font-size-2xl) * 1.5);
      font-weight: var(--font-weight-bold);
      color: var(--color-warning-text);
      line-height: 1;
    }
    .countdown__label {
      font-size: var(--font-size-sm);
      color: var(--color-text-light);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .modal__footer {
      display: flex;
      gap: var(--space-3);
      justify-content: flex-end;
    }
  `],
})
export class SessionTimeoutModalComponent {
  readonly sessionService = inject(SessionService);
  private readonly translation = inject(TranslationService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  readonly isOpen = computed(() => this.sessionService.state() === 'warning');

  readonly countdownLabel = computed(() => this.formatCountdown(this.sessionService.secondsRemaining()));

  /**
   * Texto de la región aria-live. Fuera de la ventana urgente devuelve
   * siempre el mismo string mientras el modal sigue abierto — el lector de
   * pantalla solo reacciona a CAMBIOS de texto, así que no vuelve a hablar
   * en cada tick. currentLang() se lee explícitamente para que el mensaje
   * se recalcule si el usuario cambia de idioma con el modal abierto.
   */
  readonly liveMessage = computed(() => {
    if (!this.isOpen()) return '';
    const seconds = this.sessionService.secondsRemaining();
    this.translation.currentLang();
    if (seconds <= URGENT_ANNOUNCE_THRESHOLD_SECONDS) {
      return this.translation.get('session.timeout.countdown', { seconds });
    }
    return this.translation.get('session.timeout.message');
  });

  /** Elemento que tenía el foco antes de abrirse el modal — se restaura al cerrarlo. */
  private previouslyFocused: HTMLElement | null = null;

  constructor() {
    // Foco atrapado (§2.1): al abrir, guarda el foco previo y lo mueve al
    // botón primario; al cerrar (por cualquier vía: continuar, cerrar
    // sesión, o expirar del todo), lo restaura.
    effect(() => {
      if (this.isOpen()) {
        this.previouslyFocused = document.activeElement as HTMLElement | null;
        queueMicrotask(() => this.focusPrimaryButton());
      } else if (this.previouslyFocused) {
        this.previouslyFocused.focus?.();
        this.previouslyFocused = null;
      }
    });
  }

  onContinue(): void {
    this.sessionService.renewSession();
  }

  onLogout(): void {
    this.sessionService.logout();
  }

  /** El backdrop no cierra el modal — el usuario debe elegir explícitamente. */
  onBackdropClick(): void {
    // Intencional: no cerrar al hacer clic en el backdrop.
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      // Escape equivale a "Continuar" (§2.1) — un cierre silencioso dejaría
      // al usuario sin aviso, con la sesión a punto de expirar igual.
      event.preventDefault();
      this.onContinue();
      return;
    }
    if (event.key === 'Tab') {
      this.trapTabKey(event);
    }
  }

  private trapTabKey(event: KeyboardEvent): void {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const modal = this.host.nativeElement.querySelector('.modal');
    if (!modal) return [];
    return Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  }

  private focusPrimaryButton(): void {
    this.host.nativeElement.querySelector<HTMLButtonElement>('.btn--primary')?.focus();
  }

  private formatCountdown(totalSeconds: number): string {
    const clamped = Math.max(0, totalSeconds);
    const minutes  = Math.floor(clamped / 60);
    const seconds  = clamped % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
}
